import { api } from '../lib/api.js';
import type { ClockcardsWarning } from '@attendance/shared';

/**
 * THE ERP EXCHANGE, WHICH RUNS IN TWO DIRECTIONS AND NOW HAS A PERSON IN EACH.
 *
 *   OUT  time and attendance — the CLOCKCARDSBATCH, one month at a time. Hadir
 *        is the system of record for what a worker DID, and this is the file
 *        payroll pays from. Nothing leaves without a sign-off.
 *
 *   IN   employee and contract master — payroll numbers and GL codes. The ERP
 *        is the system of record for who a worker IS and how they are paid, so
 *        an inbound value REPLACES what Hadir holds. Which is exactly why it is
 *        staged and approved rather than applied at 2am.
 *
 * The asymmetry is real and the screen shows it: one tab decides a MONTH, the
 * other decides a list of FIELDS.
 */

export type ErpBatchApprovalStatus = 'APPROVED' | 'REJECTED' | 'SUPERSEDED';

export interface ErpBatchApproval {
  id: string;
  period: string;
  status: ErpBatchApprovalStatus;
  /** The hash of the numbers that were on screen when this was decided. */
  payloadHash: string;
  rowCount: number;
  employeeCount: number;
  warningCount: number;
  incompleteWorkedMinutes: number;
  decidedById: string | null;
  decidedAt: string;
  decisionNote: string | null;
  /** Null until the export job has actually written the file. */
  releasedAt: string | null;
  releasedTo: string | null;
}

export interface ErpBatchReview {
  period: string;
  /** What a decision will be recorded against. Sent back on the decision. */
  hash: string;
  totals: {
    rowCount: number;
    employeeCount: number;
    nt: number;
    ot1Hours: number;
    ot2Hours: number;
    warningCount: number;
    incompleteWorkedMinutes: number;
    excludedIncompleteDays: number;
    excludedIncompleteMinutes: number;
  };
  warnings: ClockcardsWarning[];
  /** The decision governing this period now, or null if nobody has decided. */
  approval: ErpBatchApproval | null;
  /** Every decision ever made about it, newest first. */
  history: ErpBatchApproval[];
}

export type ErpChangeStatus = 'PENDING' | 'APPLIED' | 'REJECTED' | 'SUPERSEDED';

export interface ErpMasterChangeRow {
  id: string;
  entity: 'EMPLOYEE' | 'WORKSITE';
  field: 'ERP_PAYROLL_NUMBER' | 'GL_CODE';
  targetId: string | null;
  /** `Employee.employeeCode` or `Worksite.code` — what the FILE named. */
  targetCode: string;
  targetName: string | null;
  /** What Hadir holds. `null` is a real state: the column is unset. */
  currentValue: string | null;
  incomingValue: string;
  sourceFile: string;
  runId: string;
  status: ErpChangeStatus;
  decidedAt: string | null;
  decisionNote: string | null;
  appliedAt: string | null;
  createdAt: string;
}

export interface ErpChangeDecisionOutcome {
  applied: number;
  rejected: number;
  /** Approved, but the value had moved in Hadir. Nothing was written. */
  superseded: number;
  /** Approved, but the record is gone. Nothing was written. */
  missing: number;
}

/** Query keys, named once so a decision invalidates what it actually changed. */
export const erpBatchQueryKey = (period: string): unknown[] => ['erp', 'batch', period];
export const erpChangesQueryKey = (status: ErpChangeStatus): unknown[] => [
  'erp',
  'changes',
  status,
];
export const erpPendingChangeCountKey: unknown[] = ['erp', 'changes', 'count'];

export const erpApi = {
  /** One month, with its reconciliation figures and whatever governs it. */
  batch: (period: string) =>
    /* `api.get` returns the response BODY, not an axios response — so the
       envelope's `data` is the payload and there is one level to unwrap. */
    api.get<{ data: ErpBatchReview }>(`/erp/batches/${period}`).then((b) => b.data),

  /**
   * Clear a month to leave, or refuse it.
   *
   * `seenHash` is what the screen drew. The server rebuilds the batch, and a
   * mismatch is a 409 rather than a sign-off recorded against numbers the
   * reader never saw — the case of a correction landing while the tab was open.
   */
  decideBatch: (
    period: string,
    body: { decision: 'APPROVE' | 'REJECT'; note?: string; seenHash: string }
  ) =>
    api
      .post<{ data: ErpBatchApproval }>(`/erp/batches/${period}/decision`, body)
      .then((b) => b.data),

  changes: (status: ErpChangeStatus) =>
    api
      .get<{
        data: ErpMasterChangeRow[];
        meta?: { total?: number; cap?: number };
      }>(`/erp/changes?status=${status}`)
      .then((b) => ({
        rows: b.data,
        /* `total` is what matches the filter; `rows` is the capped page of it.
           Falling back to the page length rather than inventing a number keeps
           the bar honest on a server that sends no meta — it is what we can
           see, and it is the fallback the letters client settled on. */
        total: b.meta?.total ?? b.data.length,
        cap: b.meta?.cap ?? b.data.length,
      })),

  pendingChangeCount: () =>
    api
      .get<{ data: { pending: number } }>('/erp/changes/count')
      .then((b) => b.data.pending),

  decideChanges: (body: { ids: string[]; decision: 'APPROVE' | 'REJECT'; note?: string }) =>
    api
      .post<{ data: ErpChangeDecisionOutcome }>('/erp/changes/decision', body)
      .then((b) => b.data),
};

/* ===========================================================================
 * THE THREE THINGS THAT USED TO NEED A TERMINAL.
 *
 * Everything above is a DECISION about work the integration had already done.
 * Everything below is the work itself — the link to the ERP, the pull that
 * fills the inbound queue, and the push that hands payroll the month — and
 * until now each one was a script somebody with a shell ran.
 *
 * Shapes come from `docs/erp-screen-contract-2026-09-04.md`, the seam between
 * this screen and the two agents building the endpoints. Where the contract
 * names a field it is named here exactly; where it says only "the sync
 * report", the shape mirrors the server's own result types — see
 * `ErpSyncReport`, and the caveat on it.
 * ======================================================================== */

/** One ERP payroll, and what it is CALLED on the employee row. */
export interface ErpPayrollBinding {
  payrollId: number;
  /**
   * The `PRL` code written onto the worker and out into the CLOCKCARDSBATCH.
   * TEXT, and leading zeros are significant — `003` is not `3`. It is typed
   * rather than derived from `payrollId` for exactly that reason.
   */
  payrollNumber: string;
  label: string;
}

/** An ERP organisation, and the Hadir company its people are employed by. */
export interface ErpOrgLegalEntity {
  orgId: number;
  legalEntityId: string;
  label: string;
}

/**
 * The editable half of the connection — CONFIGURATION, and nothing else.
 *
 * There is no `userName` and no `password` here, and no field for either
 * anywhere in this screen. They are read from the server's environment because
 * settings round-trip through an admin screen, are stored as a JSON blob, and
 * are backed up more widely than a secret should be. `hasCredentials` is the
 * only thing the UI ever learns about them.
 */
export interface ErpConnectionSettings {
  enabled: boolean;
  vendor: string;
  baseUrl: string | null;
  shortName: string | null;
  orgId: number | null;
  payrolls: ErpPayrollBinding[];
  codePrefixes: { staff: string[]; worker: string[] };
  siteCodePrefixes: string[];
  orgLegalEntities: ErpOrgLegalEntity[];
}

export interface ErpConnection extends ErpConnectionSettings {
  /** Whether the environment holds a sign-in. Never the sign-in itself. */
  hasCredentials: boolean;
}

/** What a test mints a token for and reads back. It writes nothing, anywhere. */
export interface ErpConnectionTest {
  ok: boolean;
  message: string;
  /** The organisations the ERP admits to, so `orgId` is picked, not guessed. */
  orgs?: Array<{ orgId: number; name: string }>;
}

export interface ErpSyncRequest {
  /** `false` walks the identical path and writes nothing. The default. */
  apply: boolean;
  details: boolean;
  create: boolean;
  createWorksites: boolean;
  createDesignations: boolean;
}

/** Per-person profile detail — the half of the run that reads each record. */
export interface ErpDetailSyncReport {
  detailsFetched: number;
  employeesUpdated: number;
  employeesUnchanged: number;
  employeesCreated: number;
  /** People the ERP has that Hadir could NOT create, and why. Never a count. */
  notCreated: Array<{ employeeCode: string; reason: string }>;
  fieldsChanged: Record<string, number>;
  /** Coded values no mapping covers. Left unset rather than guessed. */
  unmappedValues: Array<{ field: string; value: string; count: number }>;
  /** Payrolls with no Hadir company. Their people synced without one. */
  payrollsWithoutEntity: number[];
  notes: string[];
}

/** ERP occupations against Hadir designations. */
export interface ErpDesignationSyncReport {
  occupationsRead: number;
  matched: number;
  created: string[];
  ambiguous: Array<{ occupation: string; titles: string[] }>;
  notes: string[];
}

/**
 * What a sync run reports.
 *
 * THE CONTRACT SAYS "the sync report, plus `staged: number`" AND NO MORE, so
 * the field names below are read off the server's own result types rather than
 * invented: `BuildsmartMasterSyncResult` (`services/buildsmart/masterSync.ts`)
 * at the top level, with `DetailSyncResult` and `DesignationSyncResult` under
 * `details` and `designations` the way the CLI composes them.
 *
 * EVERY FIELD BUT THE TWO THE CONTRACT NAMES IS OPTIONAL, and every section of
 * the panel is drawn only when its field actually arrived — so an envelope
 * shaped differently renders LESS rather than rendering something false.
 */
export interface ErpSyncReport {
  apply: boolean;
  /** Rows put in front of a person on the inbound tab rather than written. */
  staged: number;
  payrollsRead?: number;
  employeesRead?: number;
  /** ERP rows with no employee number — matchable by nothing, so counted. */
  employeesWithoutCode?: number;
  contracts?: {
    erpCount: number;
    matched: number;
    erpOnly: string[];
    hadirOnly: string[];
    closedInErpActiveInHadir: string[];
    created: number;
    /** Created sites have no coordinates: nobody can check in at them yet. */
    createdNeedingGeofence: string[];
  };
  leaveTypes?: { erpCount: number; matched: number; erpOnly: string[] } | null;
  payrollNumbers?: {
    existingInHadir: string[];
    writingNow: string[];
    /** A `PRL` Hadir has never seen — usually the zero-padding trap. */
    unrecognised: string[];
  };
  notes?: string[];
  details?: ErpDetailSyncReport | null;
  designations?: ErpDesignationSyncReport | null;
}

/** Mirrors `ErpDeliveryStatus` in schema.prisma, where each state is documented. */
export type ErpDeliveryStatus =
  | 'PENDING'
  | 'SENDING'
  | 'AWAITING_ACK'
  | 'ACKNOWLEDGED'
  | 'FAILED'
  | 'ABANDONED';

export interface ErpDelivery {
  id: string;
  kind: string;
  period: string;
  status: ErpDeliveryStatus;
  attempts: number;
  lastError: string | null;
  remoteReference: string | null;
  createdAt: string;
  updatedAt: string;
}

/**
 * What a push ended in.
 *
 * `AWAITING_ACK` COMES BACK ON A 202 AND IS NOT A SUCCESS. The request left and
 * nothing came back saying what the ERP did with it, so the month stays bound
 * and a person has to open the ERP and look. `axios` resolves a 202 like any
 * other 2xx — this field, never the HTTP code, is what the screen reads.
 */
export interface ErpPushOutcome {
  status: ErpDeliveryStatus;
  deliveryId: string;
  remoteReference?: string | null;
  message: string;
}

export const erpConnectionQueryKey: unknown[] = ['erp', 'connection'];
export const erpDeliveriesQueryKey: unknown[] = ['erp', 'deliveries'];

export const erpOpsApi = {
  connection: () => api.get<{ data: ErpConnection }>('/erp/connection').then((b) => b.data),

  /** PUT, so the block is replaced whole — see the note on `api.put`. */
  saveConnection: (body: ErpConnectionSettings) =>
    api.put<{ data: ErpConnection }>('/erp/connection', body).then((b) => b.data),

  testConnection: () =>
    api.post<{ data: ErpConnectionTest }>('/erp/connection/test').then((b) => b.data),

  sync: (body: ErpSyncRequest) =>
    api.post<{ data: ErpSyncReport }>('/erp/sync', body).then((b) => b.data),

  deliveries: () => api.get<{ data: ErpDelivery[] }>('/erp/deliveries').then((b) => b.data),

  /**
   * Hand the month to the ERP.
   *
   * `confirmPeriod` must equal the period exactly and the server refuses
   * anything else. It is not a formality: this is the one call in the product
   * that can put a thousand people's pay into another system.
   */
  pushBatch: (period: string, body: { confirmPeriod: string }) =>
    api.post<{ data: ErpPushOutcome }>(`/erp/batches/${period}/push`, body).then((b) => b.data),

  /**
   * A person opened the ERP and says what they found. `note` is required and is
   * their statement — a resolution with no evidence behind it is the thing this
   * endpoint exists to prevent.
   */
  resolveDelivery: (id: string, body: { outcome: 'POSTED' | 'NOT_POSTED'; note: string }) =>
    api
      .post<{ data: { status: ErpDeliveryStatus } }>(`/erp/deliveries/${id}/resolve`, body)
      .then((b) => b.data),
};
